import React, {useState, useCallback, useEffect, useMemo} from 'react'
import {View, momentLocalizer} from 'react-big-calendar'
import moment from 'moment'
import {Typography, useMediaQuery, useTheme} from '@mui/material'
import {useAppSelector} from 'src/hooks'
import {CalendarStyledNew} from './CalendarStyledNew'
import CalendarToolbarNew from './CalendarToolbarNew'
import EventDialogNew from './EventDialogNew'
import VirtualMeetingDetailNew from './VirtualMeetingDetailNew'
import {CalendarEvent, getItalianHolidayName, isItalianHoliday} from './agendaUtils'
import ActivityDetailDialogNew from 'src/components-new/feedback/ActivityDetailDialogNew'
import {isAcknowledgeableActivity, PatientAcknowledgement} from 'src/components-new/feedback/activityAckUtils'
import ActivityAckIcon from 'src/components-new/primitives/ActivityAckIcon'

// ---------------------------------------------------------------------------
// Module-level localizer (stable reference — must not be created inside render)
// ---------------------------------------------------------------------------

const localizer = momentLocalizer(moment)

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type CalendarNewProps = {
  events: CalendarEvent[]
  eventsTypes: Record<string, any>[]
  onSave: (event: CalendarEvent) => void
  onUpdate: (event: CalendarEvent, id: string | undefined) => void
  onActivityAcknowledged: (activityId: string, acknowledgement: PatientAcknowledgement) => void
  // activity whose detail opens on its own (from a web push notification, see AgendaNew)
  openedActivity?: CalendarEvent
  startDate: any
  setStartDate: any
  endDate: any
  setEndDate: any
}

// ---------------------------------------------------------------------------
// Sub-renderers (defined outside component to avoid recreating on every render)
// ---------------------------------------------------------------------------

type EventBoxProps = {event: CalendarEvent; loggedUserId?: string}

const ACK_BADGE_SX = {
  position: 'absolute',
  top: 2,
  right: 2,
  zIndex: 1,
  p: '1px',
  borderRadius: '50%',
  backgroundColor: 'background.paper',
  boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.08)',
} as const

const EventBox = ({event, loggedUserId}: EventBoxProps) => {
  const patient = event.participants?.find((u: Record<string, any>) => u.type === 'patients' && u?.id)
  const firstName = patient?.userProperties?.firstName ?? patient?.firstName
  const lastName = patient?.userProperties?.lastName ?? patient?.lastName
  return (
    <div className='event-box'>
      {isAcknowledgeableActivity(event, loggedUserId) && (
        // Corner badge instead of an inline icon: month cells on mobile are too
        // narrow to fit it next to the time label without hiding it.
        <ActivityAckIcon activity={event} size={14} sx={ACK_BADGE_SX} />
      )}
      <div>
        <div className='event-chip' />
        <Typography sx={{fontSize: '11px', fontWeight: 600}}>
          {`${moment(event.startDate).format('HH:mm')} - ${moment(event.endDate).format('HH:mm')}`}
        </Typography>
      </div>
      <Typography className='event-reason' variant='body2'>
        {firstName && lastName ? `${firstName} ${lastName} - ${event.title}` : event.title}
      </Typography>
    </div>
  )
}

const AgendaEventBox = ({event, loggedUserId}: {event: Record<string, any>; loggedUserId?: string}) => (
  <div className='event-box'>
    <Typography className='event-reason' variant='body2'>
      <span className='time-label'>{`${moment(event?.startDate).format('HH:mm')} - ${moment(event?.endDate).format('HH:mm')}`}</span>
      <span className='event-chip' />
      <span className='title-label'>{event.title}</span>
      {isAcknowledgeableActivity(event, loggedUserId) && (
        <ActivityAckIcon activity={event} size={16} sx={{ml: 1, verticalAlign: 'middle'}} />
      )}
    </Typography>
  </div>
)

const DateBox = ({day}: {day: Date}) => (
  <div className='date-box'>
    <Typography className='event-reason' variant='body2'>{moment(day).format('ddd')}</Typography>
    <Typography className='event-reason' variant='body2'>{moment(day).format('DD MMMM YYYY')}</Typography>
  </div>
)

// Month-view date number renderer — prepends a small label with the Italian
// holiday name (e.g. "Natale", "Pasquetta") to the left of the day number.
// Mirrors react-big-calendar's default DateHeader (lib/DateHeader.js) for
// the day number itself so the day-view drilldown keeps working.
const HolidayDateHeader = ({date, label, drilldownView, onDrillDown}: any) => {
  const holidayName = getItalianHolidayName(date)
  return (
    <div className='rbc-date-cell-inner'>
      {holidayName && <span className='holiday-label' title={holidayName}>{holidayName}</span>}
      {!drilldownView ? (
        <span>{label}</span>
      ) : (
        <button className='rbc-button-link' onClick={onDrillDown} role='cell' type='button'>{label}</button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Date range helpers — pure functions, no closure over component state,
// so they live at module level (not recreated on every render)
// ---------------------------------------------------------------------------

const startOfMonth = (startDateOfRange: Date) => {
  if (moment(startDateOfRange).toISOString() === moment(startDateOfRange).startOf('month').toISOString()) {
    return moment(startDateOfRange).toISOString()
  } else {
    return moment(startDateOfRange).endOf('month').toISOString()
  }
}

const endOfMonth = (endDateOfRange: Date) => {
  if (moment(endDateOfRange).toISOString() === moment(endDateOfRange).endOf('month').toISOString()) {
    return moment(endDateOfRange).toISOString()
  } else {
    return moment(endDateOfRange).startOf('month').toISOString()
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CalendarNew: React.FC<CalendarNewProps> = ({
  events,
  eventsTypes,
  onSave,
  onUpdate,
  onActivityAcknowledged,
  openedActivity,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Default to Lista (agenda) on mobile — initializer runs once at mount only
  const [calendarView, setCalendarView] = useState<View>(() => isMobile ? 'agenda' : 'month')
  const [calendarDate, setCalendarDate] = useState<Date>(new Date())
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | undefined>()
  const [isEventModal, setIsEventModal] = useState(false)
  const [isActivityModal, setIsActivityModal] = useState(false)
  const [newEvent, setNewEvent] = useState(false)
  const [openVirtualMeetingDetails, setOpenVirtualMeetingDetails] = useState(false)
  const userInfoData = useAppSelector(({userReducer}) => userReducer.data)

  useEffect(() => {
    if (!openedActivity) return
    setCalendarDate(new Date(openedActivity.startDate))
    setNewEvent(false)
    setSelectedEvent({...openedActivity})
    setIsActivityModal(true)
  }, [openedActivity])

  // -------------------------------------------------------------------------
  // Date range handler — useCallback so calendarComponents useMemo can depend
  // on a stable reference; only changes when the parent-controlled date range
  // boundaries change
  // -------------------------------------------------------------------------

  const handleDateChange = useCallback((date: Date) => {
    if (!(startOfMonth(startDate) <= moment(date).toISOString() && moment(date).toISOString() <= endOfMonth(endDate))) {
      setStartDate(moment(date).startOf('month').startOf('week'))
      setEndDate(moment(date).endOf('month').endOf('week'))
    }
    setCalendarDate(new Date(date)) // clone to avoid mutation
  }, [startDate, endDate, setStartDate, setEndDate])

  // -------------------------------------------------------------------------
  // Holiday day prop getter — adds a CSS class to Sunday/holiday cells so
  // CalendarStyledNew can swap their background with the regular weekday one.
  // Saturday is treated as a regular weekday, per client request.
  // -------------------------------------------------------------------------

  const dayPropGetter = useCallback((date: Date) => {
    if (date.getDay() === 0 || isItalianHoliday(date)) {
      return {className: 'rbc-holiday-day'}
    }
    return {}
  }, [])

  // -------------------------------------------------------------------------
  // Event selection helper
  // -------------------------------------------------------------------------

  const updateSelectedEvent = (event: any, isNew: boolean) => {
    setNewEvent(isNew)
    if (isNew) {
      setSelectedEvent({
        _id: '',
        startDate: event.start.toISOString(),
        endDate: event.end.toISOString(),
        participants: [],
        title: '',
        organizer: userInfoData,
      })
    } else {
      setSelectedEvent({...event})
    }
  }

  // -------------------------------------------------------------------------
  // Calendar components config — memoized so react-big-calendar does not
  // remount toolbar/event renderers on unrelated state changes.
  // isMobile is included so the toolbar re-renders on viewport changes.
  // -------------------------------------------------------------------------

  const calendarComponents = useMemo(() => ({
    toolbar: () => (
      <CalendarToolbarNew
        date={calendarDate}
        view={calendarView}
        handleDateChange={handleDateChange}
        handleViewChange={setCalendarView}
        isMobile={isMobile}
        onNewEvent={() => {
          setNewEvent(true)
          setSelectedEvent({
            _id: '',
            startDate: new Date().toISOString(),
            endDate: new Date().toISOString(),
            participants: [],
            title: '',
            organizer: userInfoData,
          })
          setIsEventModal(true)
        }}
      />
    ),
    week: {event: ({event}: any) => <EventBox event={event} loggedUserId={userInfoData?._id} />},
    day: {event: ({event}: any) => <EventBox event={event} loggedUserId={userInfoData?._id} />},
    month: {
      event: ({event}: any) => <EventBox event={event} loggedUserId={userInfoData?._id} />,
      dateHeader: HolidayDateHeader,
    },
    agenda: {
      event: ({event}: any) => <AgendaEventBox event={event} loggedUserId={userInfoData?._id} />,
      date: ({day}: any) => <DateBox day={day} />,
    },
  // setCalendarView/setNewEvent/setSelectedEvent/setIsEventModal are stable useState setters — intentionally omitted
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [calendarDate, calendarView, handleDateChange, userInfoData, isMobile])

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <CalendarStyledNew
        localizer={localizer}
        culture='it'
        events={events}
        view={calendarView}
        date={calendarDate}
        dayLayoutAlgorithm='no-overlap'
        selectable
        startAccessor={(event: any) => new Date(event.startDate)}
        endAccessor={(event: any) => new Date(event.endDate)}
        onNavigate={handleDateChange}
        onView={setCalendarView}
        onSelectEvent={(event: any) => {
          if (event.type === 'VIRTUAL_MEETING') {
            updateSelectedEvent(event, false)
            setOpenVirtualMeetingDetails(true)
          } else if (event.type === 'PERSONAL_EVENT') {
            updateSelectedEvent(event, false)
            setIsEventModal(true)
          } else {
            // Activities are returned by /user-events/ as raw activity documents
            // with no `type` field at all (only PERSONAL_EVENT/VIRTUAL_MEETING
            // carry an explicit type) — so anything not caught above (including
            // untyped events) opens the read-only Activity detail, mirroring the
            // legacy patient detail behavior (src/components/templates/.../Activities.tsx)
            updateSelectedEvent(event, false)
            setIsActivityModal(true)
          }
        }}
        onSelectSlot={(event: any) => {
          updateSelectedEvent(event, true)
          setIsEventModal(true)
        }}
        dayPropGetter={dayPropGetter}
        scrollToTime={new Date(new Date().setHours(7))}
        style={{height: isMobile ? 'calc(100vh - 260px)' : 'calc(100vh - 180px)'}}
        components={calendarComponents}
      />

      {isEventModal && (
        <EventDialogNew
          open={isEventModal}
          event={selectedEvent}
          eventsTypes={eventsTypes}
          onClose={() => setIsEventModal(false)}
          onSave={(values) => {
            newEvent ? onSave(values) : onUpdate(values, selectedEvent?._id)
            setIsEventModal(false)
          }}
        />
      )}

      {openVirtualMeetingDetails && (
        <VirtualMeetingDetailNew
          event={selectedEvent}
          onClose={() => setOpenVirtualMeetingDetails(false)}
        />
      )}

      {selectedEvent && (
        <ActivityDetailDialogNew
          activity={selectedEvent}
          open={isActivityModal}
          onClose={() => setIsActivityModal(false)}
          showDocuments
          onAcknowledged={(acknowledgement) => {
            setSelectedEvent((current) => current && {...current, patientAcknowledgement: acknowledgement})
            onActivityAcknowledged(selectedEvent._id, acknowledgement)
          }}
        />
      )}
    </>
  )
}

export default CalendarNew
